import { ref } from 'vue';
import AttachmentAPI from 'dashboard/api/attachments';

export const useLoadWithRetry = (config = {}) => {
  const maxRetry = config.max_retry || 3;
  const backoff = config.backoff || 500;

  const isLoaded = ref(false);
  const hasError = ref(false);

  const loadWithRetry = async (url, attachmentId = null) => {
    let currentUrl = url;

    const attemptLoad = loadUrl => {
      return new Promise((resolve, reject) => {
        const img = new Image();

        img.onload = () => {
          isLoaded.value = true;
          hasError.value = false;
          resolve();
        };

        img.onerror = () => {
          reject(new Error('Failed to load image'));
        };

        img.src = loadUrl;
      });
    };

    const sleep = ms => {
      return new Promise(resolve => {
        setTimeout(resolve, ms);
      });
    };

    const refreshUrl = async () => {
      if (!attachmentId) return currentUrl;
      try {
        const { data } = await AttachmentAPI.refreshUrl(attachmentId);
        if (data?.data_url) {
          currentUrl = data.data_url;
        }
      } catch {
        // If refresh fails, keep using current URL
      }
      return currentUrl;
    };

    const retry = async (attempt = 0) => {
      try {
        await attemptLoad(currentUrl);
      } catch (error) {
        if (attempt + 1 >= maxRetry) {
          hasError.value = true;
          isLoaded.value = false;
          return;
        }

        // After first failure, try refreshing the URL from the server
        if (attempt >= 1 && attachmentId) {
          currentUrl = await refreshUrl();
        }

        await sleep(backoff * (attempt + 1));
        await retry(attempt + 1);
      }
    };

    await retry();
  };

  return {
    isLoaded,
    hasError,
    loadWithRetry,
  };
};
